import {
    ACard,
    ADataGrid,
    ADataGridColumn,
    ADataGridFilter,
    AMultiSelectFilter,
    AMultiSelectOption,
    AMultiSelectOptionId,
    ASectionTitle,
    PagedSearchParams,
} from "@atiautomacao/ati-ui-library";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {Box, Button, IconButton, Menu, MenuItem, Stack, TextField, Tooltip} from "@mui/material";
import {EditOutlined, MoreHorizOutlined, SimCardDownloadOutlined} from "@mui/icons-material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, {Dayjs} from "dayjs";
import "dayjs/locale/pt-br";
import {DailyReportReactQuery, ExportDailyReportReactQuery} from "./reactQuery/DailyReportReactQuery";
import {FindAllPowerStationsBasicReactQuery} from "../../Settings/PowerStation/PowerStationReactQuery";
import {usePersistedSelection} from "../../../Shared/Hooks/usePersistedSelection";
import {Pill, PillTone} from "../Unavailability/Pill";
import SelectedMenu from "../Unavailability/SelectedMenu";
import DailyReportDetailsDrawer, {DailyReportDetailsRow} from "./DailyReportDetailsDrawer";
import {useAppSelector} from "../../../Config/Hooks";
import {RootState} from "../../../Config/Store";
import {useSnackbar} from "notistack";
import DailyReportPerformanceChart from "./components/DailyReportPerformanceChart";
import DailyReportTotalizers from "./components/DailyReportTotalizers";
import { POWER_STATION_IDS_FILTER_STORAGE_KEY } from "../../../Config/Constants";


const initColumns = [
    {
        name: "powerStationName",
        label: "Usina",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "powerStationName", condition: "contains", value: "", sort: "none"},
    },
    {
        name: "generatedEnergy",
        label: "Gerada (MWh)",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "generatedEnergy", condition: "equals", value: "", sort: "none"},
    },
    {
        name: "expectedEnergy",
        label: "Esperada (MWh)",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "expectedEnergy", condition: "equals", value: "", sort: "none"},
    },
    {
        name: "irradiance",
        label: "Irradiação POA (kWh/m²)",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "irradiance", condition: "equals", value: "", sort: "none"},
    },
    {
        name: "performanceRatio",
        label: "PR (%)",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "performanceRatio", condition: "equals", value: "", sort: "asc"},
    },
    {
        name: "operationAvailability",
        label: "Disponibilidade (%)",
        align: "left",
        visibility: true,
        minWidth: 100,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "operationAvailability", condition: "equals", value: "", sort: "none"},
    },
    {
        name: "diagnosis",
        label: "Diagnóstico",
        align: "left",
        visibility: true,
        minWidth: 150,
        breakpoints: {xs: true, sm: true, md: true, lg: true, xl: true},
        filter: {name: "diagnosis", condition: "contains", value: "", sort: "none"},
    },
] as ADataGridColumn[];

interface ActionConfig {
    actionName: string;
    disabled: boolean;
}

interface DailyReportRow {
    powerStationId: number;
    powerStationName: string;
    generatedEnergy: string;
    expectedEnergy: string;
    irradiance: string;
    performanceRatio: string | JSX.Element;
    operationAvailability: string;
    diagnosis: string;
    actionsConfig?: Array<ActionConfig>;
}

const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    return value.toFixed(2).replace(".", ",");
};

const verifyProgress = (value: number): PillTone => {
    if (value >= 70 && value <= 95) return "prGood";
    if (value >= 60 || value > 95) return "prWarning";
    return "prBad";
};

const renderPerformanceRatio = (value: number | null | undefined): string | JSX.Element => {
    if (value === null || value === undefined) return "-";
    return <Pill label={`${formatNumber(value)}%`} tone={verifyProgress(value)}/>;
};

export default function DailyReportPage() {
    const {enqueueSnackbar} = useSnackbar();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const [pagedSearchParams, setPagedSearchParams] = useState(
        new PagedSearchParams(extractFiltersFromColumns(initColumns), 0, userConfig.sizePageDailyReport)
    );
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
    const [rows, setRows] = useState<Array<DailyReportRow>>([]);
    const [totalOfRecords, setTotalOfRecords] = useState(0);

    const {data: powerStationsData} = FindAllPowerStationsBasicReactQuery();

    const powerStationOptions = useMemo<AMultiSelectOption[]>(() => {
        return (powerStationsData ?? []).map((ps) => ({id: ps.id, label: ps.name}));
    }, [powerStationsData]);

    const powerStationOptionIds = useMemo<AMultiSelectOptionId[]>(
        () => powerStationOptions.map((o) => o.id),
        [powerStationOptions]
    );

    const {
        selectedIds: selectedPowerStationIds,
        setSelectedIds: setSelectedPowerStationIds,
        isInitialized: powerStationsInitialized,
    } = usePersistedSelection<AMultiSelectOptionId>({
        storageKey: POWER_STATION_IDS_FILTER_STORAGE_KEY,
        allOptionIds: powerStationOptionIds,
    });

    const powerStationIdsForRequest = useMemo<number[] | null>(() => {
        if (!powerStationsInitialized) return null;
        if (selectedPowerStationIds.length === 0) return [];
        if (selectedPowerStationIds.length === powerStationOptions.length) return null;
        return selectedPowerStationIds.map((id) => Number(id));
    }, [selectedPowerStationIds, powerStationOptions, powerStationsInitialized]);

    const dateTimeISO = useMemo(() => {
        if (!selectedDate?.isValid()) return null;
        if (selectedDate.isAfter(dayjs(), "day")) return null;
        const isToday = selectedDate.isSame(dayjs(), "day");
        const upperBound = isToday ? dayjs() : selectedDate.endOf("day");
        return upperBound.format("YYYY-MM-DDTHH:mm:ss");
    }, [selectedDate]);

    useEffect(() => {
        if (selectedDate === null) return;
        if (!selectedDate.isValid()) {
            enqueueSnackbar("Data inválida", {variant: "error"});
            return;
        }
        if (selectedDate.isAfter(dayjs(), "day")) {
            enqueueSnackbar("Você só pode selecionar a data atual ou datas anteriores", {variant: "error"});
        }
    }, [selectedDate, enqueueSnackbar]);

    const {data, isSuccess, isLoading, refetch} = DailyReportReactQuery(pagedSearchParams, dateTimeISO, powerStationIdsForRequest);
    const mutationExport = ExportDailyReportReactQuery();

    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<DailyReportRow | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRow, setDetailsRow] = useState<DailyReportDetailsRow | null>(null);

    const rawRowsByIdRef = useRef<Record<number, DailyReportDetailsRow>>({});
    const lastMousePosRef = useRef<{ x: number; y: number }>({x: 0, y: 0});

    const handleMouseMoveInGrid = (e: React.MouseEvent<HTMLDivElement>) => {
        lastMousePosRef.current = {x: e.clientX, y: e.clientY};
    };

    const handleActionsClick = (value: any) => {
        setSelectedRow(value as DailyReportRow);
        const {x, y} = lastMousePosRef.current;
        setMenuPos({left: x, top: y});
    };

    const handleCloseActionsMenu = () => {
        setMenuPos(null);
    };

    const handleOpenDetails = () => {
        if (!selectedRow) return;
        const raw = rawRowsByIdRef.current[selectedRow.powerStationId];
        if (raw) {
            setDetailsRow(raw);
            setDetailsOpen(true);
        }
        handleCloseActionsMenu();
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setDetailsRow(null);
    };

    const handleExport = (format: "csv" | "excel") => {
        mutationExport.mutate({
            format,
            pagedSearchParams,
            dateTime: dateTimeISO,
            powerStationIds: powerStationIdsForRequest,
        });
    };

    const handleOnFiltersChange = useCallback(
        (dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
            const newFilters = dataGridFilters.map((f) => ({
                ...f,
                value: String(f?.value ?? "").trim(),
            }));
            setPagedSearchParams(new PagedSearchParams(newFilters, page, pageSize));
        },
        []
    );

    useEffect(() => {
        if (!isSuccess) return;
        setTotalOfRecords(data?.totalOfRecords ?? 0);
        rawRowsByIdRef.current = {};
        const dailyRows = (data?.data ?? []).map((item: any): DailyReportRow => {
            const detailsRowRaw: DailyReportDetailsRow = {
                powerStationId: item.powerStationId,
                powerStationName: item.powerStationName ?? "-",
                generatedEnergy: formatNumber(item.generatedEnergy),
                expectedEnergy: formatNumber(item.expectedEnergy),
                irradiance: formatNumber(item.irradiance),
                performanceRatio: item.performanceRatio == null ? "-" : `${formatNumber(item.performanceRatio)}%`,
                operationAvailability: item.operationAvailability == null ? "-" : `${formatNumber(item.operationAvailability)}%`,
                diagnosis: item.diagnosis ?? "",
            };
            rawRowsByIdRef.current[item.powerStationId] = detailsRowRaw;
            return {
                powerStationId: item.powerStationId,
                powerStationName: item.powerStationName ?? "-",
                generatedEnergy: formatNumber(item.generatedEnergy),
                expectedEnergy: formatNumber(item.expectedEnergy),
                irradiance: formatNumber(item.irradiance),
                performanceRatio: renderPerformanceRatio(item.performanceRatio),
                operationAvailability: formatNumber(item.operationAvailability),
                diagnosis: item.diagnosis ?? "-",
                actionsConfig: [{actionName: "edit", disabled: false}],
            };
        });
        setRows(dailyRows);
    }, [data, isSuccess]);

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1,
                    ml: 3,
                    mr: 3,
                }}
            >
                <Box sx={{flex: 1, minWidth: 0}}>
                    <ASectionTitle
                        titleLabel="Acompanhamento Diário"
                        subTitleLabel="Resumo operacional diário das usinas"
                        contentChildren={
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                <Stack direction="row" spacing={1.5} sx={{flexShrink: 0, mt: 1, alignItems: 'center'}}>
                                    <AMultiSelectFilter
                                        options={powerStationOptions}
                                        selectedIds={selectedPowerStationIds}
                                        onApply={setSelectedPowerStationIds}
                                        triggerLabel="Usinas"
                                        searchPlaceholder="Buscar usina..."
                                    />
                                    <DatePicker
                                        label="Data"
                                        inputFormat="DD/MM/YYYY"
                                        value={selectedDate}
                                        maxDate={dayjs()}
                                        onChange={(newValue: Dayjs | null) => setSelectedDate(newValue)}
                                        renderInput={(params: any) => (
                                            <TextField size="small" sx={{width: 170}} {...params} />
                                        )}
                                    />
                                </Stack>
                            </LocalizationProvider>
                        }
                    />
                </Box>
            </Box>

            <Box sx={{mx: 3, mb: 2}}>
                <DailyReportTotalizers
                    selectedDate={dateTimeISO}
                    powerStationIds={powerStationIdsForRequest}
                />
            </Box>

            <Box sx={{m: 3}}>
                <DailyReportPerformanceChart
                    selectedDate={dateTimeISO}
                    powerStationIds={powerStationIdsForRequest}
                />
            </Box>

            <Box
                onMouseMove={handleMouseMoveInGrid}
                sx={{
                    m: '0 24px 24px 24px',
                    "& .MuiCardHeader-root": {display: "none"},
                    "& .MuiCardContent-root": {
                        padding: "0 !important",
                    },
                }}
            >
                <ACard>
                    <Box sx={{position: "relative"}}>
                        <ADataGrid
                            ariaLabel={"daily-report-grid"}
                            hideFilters={false}
                            showFilterOptions={false}
                            hideSelection={true}
                            columns={initColumns}
                            rows={rows}
                            page={pagedSearchParams?.page}
                            disableActionsHeaderIcons={true}
                            rowsPerPage={pagedSearchParams?.size}
                            loading={isLoading}
                            totalOfRecords={totalOfRecords}
                            onFiltersChange={handleOnFiltersChange}
                            actions={
                                <>
                                    <span></span>
                                    <IconButton
                                        name="edit"
                                        size="small"
                                        aria-label="Ações"
                                        onClick={(value) => handleActionsClick(value)}
                                    >
                                        <Tooltip title="Opções">
                                            <MoreHorizOutlined/>
                                        </Tooltip>
                                    </IconButton>
                                </>
                            }
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                left: 16,
                                bottom: 6,
                                display: "flex",
                                gap: 1,
                                zIndex: 1,
                            }}
                        >
                            <Button
                                size="medium"
                                variant="text"
                                startIcon={<SimCardDownloadOutlined fontSize="large"/>}
                                onClick={() => handleExport("csv")}
                                disabled={mutationExport.isLoading || totalOfRecords === 0}
                            >
                                CSV
                            </Button>
                            <Button
                                size="medium"
                                variant="text"
                                startIcon={<SimCardDownloadOutlined fontSize="large"/>}
                                onClick={() => handleExport("excel")}
                                disabled={mutationExport.isLoading || totalOfRecords === 0}
                            >
                                Excel
                            </Button>
                        </Box>
                    </Box>
                </ACard>
            </Box>

            <Menu
                open={Boolean(menuPos && selectedRow)}
                onClose={handleCloseActionsMenu}
                anchorReference="anchorPosition"
                anchorPosition={menuPos ? {top: menuPos.top, left: menuPos.left} : undefined}
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        minWidth: 220,
                        boxShadow: "0px 6px 18px rgba(0,0,0,0.12)",
                        overflow: "hidden",
                        mt: 1,
                    },
                }}
            >
                <MenuItem onClick={handleOpenDetails} sx={{py: 1.25}}>
                    <SelectedMenu label="Editar Diagnóstico" icon={<EditOutlined fontSize="small"/>}/>
                </MenuItem>
            </Menu>

            <DailyReportDetailsDrawer
                open={detailsOpen}
                row={detailsRow}
                dateTime={dateTimeISO}
                onClose={handleCloseDetails}
                onAfterSave={refetch}
            />
        </>
    );
}
